import axios from "axios"
import {getStationCode} from "../../network/NetworkUtil";
import {convertSecondsToHHMM, getDateInMMDDYYYYWithOffset} from "../../util/Util";
import {getAPIGatewayDataFromParent} from "../../handler/ParentDataHandler";
import {
    IVS_GET_PACKAGE_METRIC,
    logOperationalMetric, MetricType,
    ROUTE_TAB_METRIC_KEY,
    RSWS_GET_PACKAGE_METRIC,
    RSWS_ROUTE_LIST_METRIC,
    SVS_DRS_ROUTES_METRIC,
    SVS_GET_PACKAGE_METRIC
} from "../../util/MetricUtil";
import {TIMEOUTS} from "../../util/Constants";
import {Logger} from "../../util/Logger";

/** Get Core Route Information from RSWS **/
export function fetchRoutes(dispatcher, isCore, baseUrl, capability, stationConfig) {
    isCore ?
        getCoreRoutes(dispatcher, baseUrl, capability, stationConfig) :
        getNonCoreRoutes(dispatcher, baseUrl, capability, stationConfig)
}

/** Get Core Routes Information **/
async function getCoreRoutes(dispatcher, baseUrl, capability, stationConfig) {
    const startTime = Date.now();
    const stationCode = getStationCode();
    try {
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_GET_STATION_ROUTES_API_TIMEOUT
        });
        var response;
        if (stationConfig && stationConfig.authEnabled) {
            const requestBody = {
                "date": [getDateInMMDDYYYYWithOffset(0)]
            }
            const requestPayload = {
                resourcePath: "/rsws/route/station/" + stationCode,
                httpMethod: "get",
                processName: "routeStaging",
                requestParams: requestBody
            }
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.get("/rsws/route/station/" + stationCode + "?date="+ getDateInMMDDYYYYWithOffset(0));
        }
        const output = response.data.routes;
        logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, RSWS_ROUTE_LIST_METRIC, MetricType.SUCCESS);
        dispatcher({type: "RECEIVE_DATA", data: getCoreRouteMetaData(dispatcher, baseUrl, output, capability, stationConfig)});
    } catch (error) {
        Logger.error("Exception in RouteNetworkAPI.getCoreRoutes", error);
        logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, RSWS_ROUTE_LIST_METRIC, MetricType.FAILED);
    }
}

function getCoreRouteMetaData(dispatcher, baseUrl, output, capability, stationConfig) {
    const model = {
        routeState: {
            inducted: output.length,
            stowed: 0,
            picked: 0,
            staged: 0,
            departed: 0
        },
        routeList: [],
        metricAvailability: {
            pick: false,
            stage: false,
            depart: false,
            stow: false,
            induct: false
        },
    }
    const routeIds = [];
    output.forEach(route => {
        try {
            const routeDetail = {
                routeId: route.routeId,
                routeCode: route.routeCode,
                routeSLA: convertSecondsToHHMM(route.criticalPullTime),
                inducted: -1,
                stowed: -1,
                picked: -1,
                staged: -1,
                departed: -1,
                problemSolve: -1,
                planned: -1
            }
            model.routeList.push(routeDetail)
            routeIds.push(route.routeId)
        } catch (e) {
        }
    })
    getRoutesPickPackageCount(dispatcher, baseUrl, routeIds, capability, stationConfig)
    getRoutesInductedPackageCount(dispatcher, routeIds, capability)
    getRoutesStowedPackageCount(dispatcher, routeIds, capability)
    return model
}

/** Get Non Core Routes Information **/
function getNonCoreRoutes(dispatcher, baseUrl, capability, stationConfig) {
    const request = {
        filters: {
            NODE:[getStationCode()]
        },
        fieldsRequired: ["ID", "CODE", "PLANNED_COUNT", "CPT"]
    }
    getDRSRouteDetails(dispatcher, request, baseUrl, capability, stationConfig)
}

function getStowRouteAPIGatewayRequest(request) {
    return {
        resourcePath: "/svs/routes/data",
        httpMethod: "post",
        processName: "stow",
        requestBody: request
    }
}

async function getDRSRouteDetails(dispatcher, request, baseUrl, capability, stationConfig) {
    const startTime = Date.now();
    const stationCode = getStationCode();
    const args = getStowRouteAPIGatewayRequest(request);
    getAPIGatewayDataFromParent(args).then(result => {
        if (result.status === 200) {
            logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, SVS_DRS_ROUTES_METRIC, MetricType.SUCCESS);
            const output = result.data.routes
            dispatcher({type: "RECEIVE_NON_CORE_ROUTE", data:getNonCoreRouteData(dispatcher, output, baseUrl, capability, stationConfig)})
        } else {
            logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, SVS_DRS_ROUTES_METRIC, MetricType.FAILED);
        }
    })
}

function getNonCoreRouteData(dispatcher, output, baseUrl, capability, stationConfig) {
    const model = {
        routeState: {
            inducted: 0,
            stowed: 0,
            picked: 0,
            staged: 0,
            departed: 0
        },
        routeList: [],
        metricAvailability: {
            pick: false,
            stage: false,
            depart: false,
            stow: false,
            induct: false
        }
    }
    const routeIds = [];
    output.forEach(route => {
        try {
            const routeDetail = {
                routeId: route.id,
                routeCode: route.code,
                routeSLA: convertSecondsToHHMM(route.cpt),
                inducted: -1,
                stowed: -1,
                picked: -1,
                staged: -1,
                departed: -1,
                problemSolve: -1,
                planned: route.plannedCount
            }
            model.routeList.push(routeDetail)
            routeIds.push(route.id)
        } catch (e) {
        }
    })
    getRoutesPickPackageCount(dispatcher, baseUrl, routeIds, capability, stationConfig)
    getRoutesInductedPackageCount(dispatcher, routeIds, capability)
    getRoutesStowedPackageCount(dispatcher, routeIds, capability)
    return model
}

/** Get Picked/Stage/Departed Count from RSWS **/
async function getRoutesPickPackageCount(dispatcher, baseUrl, routeIds, capability, stationConfig) {
    const startTime = Date.now();
    const stationCode = getStationCode();
    const requestBody = {
        "stationCode": stationCode,
        "date": getDateInMMDDYYYYWithOffset(0),
        "metricList": ["PICKED", "STAGED", "DEPARTED", "PROBLEM_SOLVE"],
        "groupBy":"ROUTE",
        "filters": {
            routeId: routeIds
        }
    }
    try {
        const api = axios.create({
            baseURL: baseUrl,
            timeout: TIMEOUTS.RSWS_DEFAULT_API_TIMEOUT
        });
        var response;
        if (stationConfig && stationConfig.authEnabled) {
            const requestPayload = {
                resourcePath: "/rsws/package/metric",
                httpMethod: "post",
                processName: "routeStaging",
                requestBody: requestBody
            }
            response = await getAPIGatewayDataFromParent(requestPayload);
        } else {
            response = await api.post("/rsws/package/metric", requestBody);
        }
        logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, RSWS_GET_PACKAGE_METRIC, MetricType.SUCCESS);
        const output = response.data.values;
        dispatcher({type: "RECEIVE_PICK_DATA", data:getPickStateData(output, capability)})
    } catch (error) {
        Logger.error("Exception in RouteNetworkAPI.getRoutesPickPackageCount", error);
        logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, RSWS_GET_PACKAGE_METRIC, MetricType.FAILED);
    }
}

function getPickStateData(output, capability) {
    const model = {
        pickData: [],
        capability: capability
    }
    output.forEach(data => {
        const value = {
            routeId: data.id,
            inducted: 0,
            picked: 0,
            staged: 0,
            departed: 0,
            problemSolve: 0
        }
        data.metricList.forEach(dataMetric => {
            switch (dataMetric.metric) {
                case "PICKED":
                    value.picked = dataMetric.metricValue;
                    break;
                case "STAGED":
                    value.staged = dataMetric.metricValue;
                    break;
                case "DEPARTED":
                    value.departed = dataMetric.metricValue;
                    break;
                case "PROBLEM_SOLVE":
                    value.problemSolve = dataMetric.metricValue;
                    break;
                default:
                    break;
            }
        })
        model.pickData.push(value);
    });
    return model;
}

/** Get Inducted Count from IVS **/
function getRoutesInductedPackageCount(dispatcher, routeIds, capability) {
    const request = {
        nodeId: getStationCode(),
        groupBy: 'RouteId',
        metricList: ["INDUCTED"],
        filters: {
            RouteId: routeIds
        },
    }
    getInductPackageMetric(dispatcher, request, capability)
}

function getInductAPIGatewayRequest(request) {
    return {
        resourcePath: "/ivs/getPackageMetric",
        httpMethod: "post",
        processName: "induct",
        requestBody: request
    }
}

async function getInductPackageMetric(dispatcher, request, capability) {
    const startTime = Date.now();
    const stationCode = getStationCode();
    const args = getInductAPIGatewayRequest(request);

    getAPIGatewayDataFromParent(args).then(result => {
        if (result.status === 200) {
            try {
                logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, IVS_GET_PACKAGE_METRIC, MetricType.SUCCESS);
                const output = result.data.groupedPackageMetrics["INDUCTED"]
                dispatcher({type: "RECEIVE_INDUCT_DATA", data: getInductedData(output, capability)})
            } catch (error) {
                Logger.error("Exception in RouteNetworkAPI.getInductPackageMetric", error);
                logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, IVS_GET_PACKAGE_METRIC, MetricType.FAILED);
            }

        } else {
            logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, IVS_GET_PACKAGE_METRIC, MetricType.FAILED);
        }
    })
}

function getInductedData(output, capability) {
    const model = {
        inductData: [],
        capability: capability
    }
    Object.entries(output).forEach(([key, value]) => {
        model.inductData.push({
            routeId: key,
            inducted: value
        })
    })
    return model;
}

/** Get Stowed Count from SVS **/
function getRoutesStowedPackageCount(dispatcher, routeIds, capability) {
    const request = {
        filters: {
            NODE:[getStationCode()],
            ROUTE_ID: routeIds
        },
        groupBy: "ROUTE_ID",
        metrics: ["CURRENT_STOWED_COUNT"]
    }
    getStowPackageMetric(dispatcher, request, capability)
}

function getStowAPIGatewayRequest(request) {
    return {
        resourcePath: "/svs/packages/metrics",
        httpMethod: "post",
        processName: "stow",
        requestBody: request
    }
}

async function getStowPackageMetric(dispatcher, request, capability) {
    const startTime = Date.now();
    const stationCode = getStationCode();
    const args = getStowAPIGatewayRequest(request);
    getAPIGatewayDataFromParent(args).then(result => {
        if (result.status === 200) {
            try {
                logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, SVS_GET_PACKAGE_METRIC, MetricType.SUCCESS);
                const output = result.data.groupedPackageMetrics
                dispatcher({type: "RECEIVE_STOW_DATA", data:getStowedData(output, capability)})
            } catch (error) {
                Logger.error("Exception in RouteNetworkAPI.getStowPackageMetric", error);
                logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, SVS_GET_PACKAGE_METRIC, MetricType.FAILED);
            }

        } else {
            logOperationalMetric(startTime, stationCode, ROUTE_TAB_METRIC_KEY, SVS_GET_PACKAGE_METRIC, MetricType.FAILED);
        }
    })
}

function getStowedData(output, capability) {
    const model = {
        stowData: [],
        capability: capability
    }
    Object.entries(output).forEach(([key, value]) => {
        model.stowData.push({
            routeId: key,
            stowed: value["currentStowedCount"]
        })
    })
    return model;
}


// WEBPACK FOOTER //
// ./src/components/routeView/network/RouteNetworkAPI.js